/**
 * Component: SeasonalMenuCard
 * Author: Dine AI
 * Date: July 16, 2025
 * Last Modified: December 17, 2025 - Light mode conversion
 *
 * Purpose:
 * - Manages seasonal menu creation and activation
 * - Displays plan-based menu limits and usage
 * - Handles menu toggling (only one active at a time)
 * - Shows seasonal icons based on menu dates
 * - Provides quick access to seasonal menu features
 */
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Snowflake,
  Sun,
  Leaf,
  Flower2,
  Calendar,
  Plus,
  ArrowRight,
  Lock,
  AlertCircle
} from 'lucide-react';
import { apiClient } from '@/services/api/apiClient';
import { useToast } from '@/components/ui/use-toast';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Switch } from '@/components/ui/switch';

interface SeasonalMenu {
  seasonal_menu_id: number;
  menu_name: string;
  description?: string;
  start_date?: string;
  end_date?: string;
  is_active: boolean;
  item_count: number;
}

interface SeasonalMenuUsage {
  plan: string;
  limit: number;
  active_count: number;
  total_count: number;
  can_create: boolean;
}

const SeasonalMenuCard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [menus, setMenus] = useState<SeasonalMenu[]>([]);
  const [usage, setUsage] = useState<SeasonalMenuUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [togglingId, setTogglingId] = useState<number | null>(null);

  // Toggle confirmation modal state
  const [showToggleDialog, setShowToggleDialog] = useState(false);
  const [pendingToggle, setPendingToggle] = useState<{
    menuId: number;
    currentStatus: boolean;
    menuName: string;
  } | null>(null);

  useEffect(() => {
    // Retry logic for page refresh scenarios
    const attemptFetch = async () => {
      try {
        await fetchSeasonalMenus();
      } catch (error) {
        // If initial fetch fails, retry once after a delay
        console.log('[SeasonalMenuCard] Initial fetch failed, retrying...');
        setTimeout(() => fetchSeasonalMenus(), 1000);
      }
    };

    attemptFetch();
  }, []);

  const fetchSeasonalMenus = async () => {
    try {
      console.log('[SeasonalMenuCard] Fetching seasonal menus and usage');
      setLoading(true);

      // Add a small delay to ensure auth token is ready after page refresh
      const token = Cookies.get('auth_token');
      if (!token) {
        console.log('[SeasonalMenuCard] No auth token found, waiting...');
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      const [menusResponse, usageResponse] = await Promise.all([
        apiClient.get('/seasonal-menus', { params: { include_inactive: true } }),
        apiClient.get('/seasonal-menus/usage')
      ]);

      setMenus(menusResponse.data);
      setUsage(usageResponse.data);
      console.log('[SeasonalMenuCard] Data loaded:', { menus: menusResponse.data, usage: usageResponse.data });
    } catch (error: any) {
      console.error('[SeasonalMenuCard] Error fetching seasonal menus:', error);
      // Only show error toast if it's not a 401 (handled by apiClient interceptor)
      if (error.response?.status !== 401) {
        toast({
          title: "Error",
          description: "Failed to load seasonal menus",
          variant: "destructive"
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleToggleClick = (menuId: number, currentStatus: boolean, menuName: string) => {
    // Show confirmation dialog
    setPendingToggle({ menuId, currentStatus, menuName });
    setShowToggleDialog(true);
  };

  const handleToggleConfirm = async () => {
    if (!pendingToggle) return;

    const { menuId, currentStatus } = pendingToggle;
    const targetStatus = !currentStatus;
    const action = targetStatus ? 'activate' : 'deactivate';

    setShowToggleDialog(false);

    try {
      setTogglingId(menuId);

      // Call the API
      const response = await apiClient.put(`/seasonal-menus/${menuId}/toggle`, null, {
        params: { is_active: targetStatus }
      });

      // Verify the response contains the expected is_active state
      if (response.data && response.data.is_active !== targetStatus) {
        console.warn(`[SeasonalMenuCard] Toggle response mismatch. Expected: ${targetStatus}, Got: ${response.data.is_active}`);
      }

      // Refresh the menu list to sync with backend
      await fetchSeasonalMenus();

      toast({
        title: "Success",
        description: targetStatus ? 'Menu activated successfully' : 'Menu deactivated successfully',
      });

    } catch (error: any) {
      console.error('[SeasonalMenuCard] Toggle error:', error);

      // Refresh menu list to ensure UI matches backend state
      await fetchSeasonalMenus();

      toast({
        title: "Error",
        description: error.response?.data?.detail || `Failed to ${action} menu`,
        variant: "destructive"
      });
    } finally {
      setTogglingId(null);
      setPendingToggle(null);
    }
  };

  const handleToggleCancel = () => {
    setShowToggleDialog(false);
    setPendingToggle(null);
  };

  const handleCreateMenu = () => {
    navigate('/seasonal-menu/create');
  };

  const handleEditMenu = (menuId: number) => {
    navigate(`/seasonal-menu/${menuId}/edit`);
  };

  const getSeasonIcon = (menuName: string) => {
    const name = menuName.toLowerCase();
    if (name.includes('winter') || name.includes('holiday')) return <Snowflake className="h-5 w-5" />;
    if (name.includes('summer')) return <Sun className="h-5 w-5" />;
    if (name.includes('fall') || name.includes('autumn')) return <Leaf className="h-5 w-5" />;
    if (name.includes('spring')) return <Flower2 className="h-5 w-5" />;
    return <Calendar className="h-5 w-5" />;
  };

  const getLimitDisplay = () => {
    if (!usage) return '';
    if (usage.limit === -1) return 'Unlimited';
    return `${usage.active_count} of ${usage.limit} used`;
  };

  const canCreateMore = usage?.can_create ?? false;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm h-full flex flex-col"
    >
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
        <div>
          <h3 className="text-xl font-semibold text-gray-900 mb-1">Special Menus</h3>
          <p className="text-gray-500 text-sm">Create menus for holidays, brunch, happy hour, or events</p>
        </div>
        {usage && (
          <span className="px-3 py-1 bg-gray-100 rounded-full text-gray-600 text-sm whitespace-nowrap border border-gray-200">
            {getLimitDisplay()}
          </span>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center flex-1">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FDB913]" />
        </div>
      ) : (
        <div className="flex flex-col flex-1">
          {/* Active Menus List */}
          <div className="flex-1 flex flex-col">
            {menus.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center">
                <Calendar className="h-12 w-12 text-gray-300 mb-3" />
                <p className="text-gray-500">No special menus created yet</p>
              </div>
            ) : (
              <div className="space-y-2 overflow-y-auto flex-1 mb-4" style={{ maxHeight: '120px' }}>
                {menus.map((menu) => (
                  <motion.div
                    key={menu.seasonal_menu_id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className={`flex items-center justify-between p-3 rounded-xl border transition-all ${
                      menu.is_active
                        ? 'bg-yellow-50 border-yellow-200'
                        : 'bg-gray-50 border-gray-200'
                    }`}
                  >
                    <div
                      className="flex items-center gap-2 flex-1 cursor-pointer min-w-0"
                      onClick={() => handleEditMenu(menu.seasonal_menu_id)}
                    >
                      <div className={`p-1.5 rounded-lg flex-shrink-0 ${
                        menu.is_active ? 'bg-yellow-100' : 'bg-gray-100'
                      }`}>
                        <div className={menu.is_active ? 'text-yellow-600' : 'text-gray-400'}>
                          {getSeasonIcon(menu.menu_name)}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className={`font-medium truncate text-sm ${menu.is_active ? 'text-gray-900' : 'text-gray-500'}`}>
                          {menu.menu_name}
                          {!menu.is_active && <span className="ml-2 text-xs text-gray-400">(Inactive)</span>}
                        </h4>
                        <p className={menu.is_active ? 'text-gray-500 text-xs' : 'text-gray-400 text-xs'}>
                          {menu.item_count} items
                        </p>
                      </div>
                    </div>

                    {/* Toggle Switch with Label */}
                    <div
                      className="flex items-center gap-3 flex-shrink-0"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <span className={`text-xs font-medium ${menu.is_active ? 'text-green-600' : 'text-gray-400'}`}>
                        {menu.is_active ? 'Active' : 'Inactive'}
                      </span>
                      {togglingId === menu.seasonal_menu_id ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-600" />
                      ) : (
                        <Switch
                          checked={menu.is_active}
                          onCheckedChange={() => handleToggleClick(menu.seasonal_menu_id, menu.is_active, menu.menu_name)}
                          disabled={togglingId === menu.seasonal_menu_id}
                          className={`scale-125 ${
                            menu.is_active
                              ? 'data-[state=checked]:bg-green-500'
                              : 'data-[state=unchecked]:bg-gray-300'
                          }`}
                        />
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>

          {/* Create Button - At the bottom */}
          <div className="mt-auto">
            {canCreateMore ? (
              <button
                onClick={handleCreateMenu}
                className="w-full py-3 bg-[#FDB913] text-black rounded-xl font-medium hover:bg-[#e5a711] transition-all flex items-center justify-center gap-2"
              >
                <Plus className="h-5 w-5" />
                Create Special Menu
              </button>
            ) : (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-gray-900 font-medium text-sm">Plan limit reached</p>
                    <p className="text-gray-500 text-xs mt-0.5">
                      Your {usage?.plan} plan allows {usage?.limit} special menu{usage?.limit === 1 ? '' : 's'} per location.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Toggle Confirmation Dialog */}
      <AlertDialog open={showToggleDialog} onOpenChange={setShowToggleDialog}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingToggle && !pendingToggle.currentStatus ? 'Activate Menu' : 'Deactivate Menu'}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-600">
              {pendingToggle && !pendingToggle.currentStatus
                ? 'Activate this menu? It will be visible to customers and the menu assistant.'
                : 'Deactivate this menu? It will be hidden from customers and the menu assistant until reactivated.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleToggleCancel}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleToggleConfirm}
              className="bg-[#FDB913] hover:bg-[#e5a711] text-black font-semibold"
            >
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </motion.div>
  );
};

export default SeasonalMenuCard;
